import type { ServerEvent } from '@map/shared';

export const BACKOFF_BASE_MS = 500;
export const BACKOFF_MAX_MS = 30_000;

/**
 * Recul exponentiel plafonne. Le plafond de 30 s evite qu'un serveur en cours
 * de redemarrage soit martele par cinq onglets ouverts, tout en gardant une
 * reprise assez rapide pour que personne ne remarque une coupure de quelques
 * secondes.
 */
export function nextBackoffMs(attempt: number): number {
  const raw = BACKOFF_BASE_MS * 2 ** attempt;
  return Number.isFinite(raw) ? Math.min(BACKOFF_MAX_MS, raw) : BACKOFF_MAX_MS;
}

export type RealtimeStatus = 'idle' | 'connecting' | 'live' | 'retrying';

export interface RealtimeOptions {
  /** URL du canal. Par defaut construite depuis `location` (same-origin). */
  readonly url?: string;
  readonly onEvent: (event: ServerEvent) => void;
  /**
   * Appele a chaque reouverture APRES la premiere. Le store y declenche un
   * `GET /api/state` complet : rejouer les evenements manques demanderait un
   * journal cote serveur, alors que l'etat entier tient dans une reponse.
   */
  readonly onReconnect: () => void;
  readonly onStatus?: (status: RealtimeStatus) => void;
  /** Point d'injection pour les tests ; en production, le WebSocket natif. */
  readonly createSocket?: (url: string) => WebSocket;
}

export interface Realtime {
  start(): void;
  stop(): void;
  readonly status: RealtimeStatus;
}

const EVENT_TYPES = new Set<string>([
  'member:joined',
  'place:created',
  'place:updated',
  'place:deleted',
  'vote:changed',
  'vote:removed',
  'comment:added',
  'comment:deleted',
  'photo:added',
  'photo:deleted',
  'itinerary:reordered',
]);

function parseEvent(raw: unknown): ServerEvent | null {
  if (typeof raw !== 'string') return null;
  let payload: unknown;
  try {
    payload = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof payload !== 'object' || payload === null) return null;
  const type = (payload as Record<string, unknown>)['type'];
  if (typeof type !== 'string' || !EVENT_TYPES.has(type)) return null;
  return payload as ServerEvent;
}

function defaultUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${scheme}//${window.location.host}/api/ws`;
}

export function createRealtime(options: RealtimeOptions): Realtime {
  const open = options.createSocket ?? ((url: string) => new WebSocket(url));

  let socket: WebSocket | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let stopped = true;
  let everOpened = false;
  let status: RealtimeStatus = 'idle';

  function setStatus(next: RealtimeStatus): void {
    if (status === next) return;
    status = next;
    options.onStatus?.(next);
  }

  function scheduleRetry(): void {
    if (stopped || timer !== null) return;
    setStatus('retrying');
    const wait = nextBackoffMs(attempt);
    attempt += 1;
    timer = setTimeout(() => {
      timer = null;
      connect();
    }, wait);
  }

  function connect(): void {
    if (stopped || socket !== null) return;
    setStatus('connecting');

    let next: WebSocket;
    try {
      next = open(options.url ?? defaultUrl());
    } catch {
      scheduleRetry();
      return;
    }
    socket = next;

    next.onopen = () => {
      if (socket !== next) return;
      attempt = 0;
      setStatus('live');
      // La toute premiere ouverture n'est pas une reconnexion : l'ecran vient
      // deja de charger l'etat via GET /api/state, resynchroniser ferait un
      // aller-retour inutile au demarrage.
      if (everOpened) options.onReconnect();
      everOpened = true;
    };

    next.onmessage = (message: MessageEvent<unknown>) => {
      if (socket !== next) return;
      const event = parseEvent(message.data);
      if (event !== null) options.onEvent(event);
    };

    next.onclose = () => {
      if (socket !== next) return;
      socket = null;
      if (stopped) {
        setStatus('idle');
        return;
      }
      scheduleRetry();
    };

    next.onerror = () => {
      // `onclose` suit toujours une erreur : programmer la reconnexion ici
      // ferait partir deux timers pour une seule coupure.
      if (socket === next) setStatus('retrying');
    };
  }

  return {
    start(): void {
      if (!stopped) return;
      stopped = false;
      attempt = 0;
      connect();
    },
    stop(): void {
      stopped = true;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      const current = socket;
      socket = null;
      current?.close();
      setStatus('idle');
    },
    get status(): RealtimeStatus {
      return status;
    },
  };
}
